import type { VideoStreamingStatus } from './NativeVideoHost';
import { nativeVideoRejectReason } from './NativeVideoReject';

/**
 * Debug-overlay line describing which video path a viewer is actually on, e.g.
 * `video native/webcodecs h264 (req h264)`, `video native/webcodecs hevc · recovering keyframe`,
 * `video native/webcodecs hevc · restart 3 · last: <reason>`, `video native/webcodecs hevc starting…`,
 * `video native/webcodecs · blocked: guest-unsupported`, `video electron → native h264 starting…`,
 * `video electron · fallback: guest-unsupported`, `video electron · reject: camera-not-allowed`,
 * or `video -`.
 *
 * For RTP playback `active` is the handoff truth (the host only flips to `native` after the guest's
 * `ready` suspended conventional video). WebCodecs playback is exclusive: Electron video is off from
 * the start, so the viewer is `native` while it starts, restarts or is blocked.
 * IPC rejects use `reason` (not terminal `fallbackReason`) so WebCodecs can still restart.
 */
export function formatVideoPathDebugLine(status: VideoStreamingStatus | undefined): string {
  if (!status) return 'video -';
  const requested = status.requestedCodec ? ` (req ${status.requestedCodec})` : '';
  if (status.active === 'native') {
    const playback = status.playback ?? 'rtp';
    const codec = status.codec ?? '?';
    const last = status.reason ? ` · last: ${status.reason}` : '';
    if (status.fallbackReason) return `video native/${playback} · blocked: ${status.fallbackReason}`;
    if (status.recoveryState === 'keyframe' || status.recoveryState === 'reset')
      return `video native/${playback} ${codec} · recovering ${status.recoveryState}`;
    if (status.recoveryState === 'restart') return `video native/${playback} ${codec} · restart ${status.sessionRestarts}${last}`;
    if (status.pending) return `video native/${playback} ${status.codec ?? status.requestedCodec ?? '?'} starting…${last}`;
    return `video native/${playback} ${codec}${requested}`;
  }
  if (status.fallbackReason) return `video electron · fallback: ${status.fallbackReason}`;
  const reject = nativeVideoRejectReason(status.reason);
  if (reject) return `video electron · reject: ${reject}`;
  if (status.pending) return `video electron → native ${status.requestedCodec ?? '?'} starting…`;
  return 'video electron';
}

/** Fields whose change constitutes a video-path transition worth a log line. */
const describe = (status: VideoStreamingStatus): string =>
  [status.active, status.pending, status.playback, status.requestedCodec, status.codec, status.reason,
    status.recoveryState, status.sessionRestarts, status.fallbackReason].join('|');

/**
 * Diffs successive `NativeVideoHost.getStatus()` snapshots so a 1 Hz poll logs transitions
 * only: new viewers, any change of active path / pending / codec / reason. Viewers that
 * disappear are forgotten, so a reconnecting peer logs afresh.
 */
export class VideoPathStatusTracker {
  private readonly seen = new Map<string, string>();

  update(statuses: VideoStreamingStatus[]): VideoStreamingStatus[] {
    const changed: VideoStreamingStatus[] = [];
    const present = new Set<string>();
    for (const status of statuses) {
      present.add(status.viewerId);
      const next = describe(status);
      if (this.seen.get(status.viewerId) === next) continue;
      this.seen.set(status.viewerId, next);
      changed.push({ ...status });
    }
    for (const viewerId of [...this.seen.keys()]) if (!present.has(viewerId)) this.seen.delete(viewerId);
    return changed;
  }
}
